import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { InquiryHero } from './partner/formUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const BOOKING_URL = 'https://cal.com/yogeshgawale';

const post = async (path, body) => {
  const r = await fetch(`${API}/api/cda/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  let data = {};
  try { data = await r.json(); } catch (e) { /* ignore */ }
  if (!r.ok || data.ok === false) throw new Error(data.error || 'Something went wrong. Please try again.');
  return data;
};

const card = 'bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10';
const input = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-teal-600';
const label = 'block text-sm font-medium text-gray-800 mb-1';
const btnPrimary = 'inline-flex items-center justify-center rounded-md bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60 disabled:cursor-not-allowed';
const btnOrange = 'inline-flex items-center justify-center rounded-md bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60';
const btnGhost = 'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50';

const Field = ({ id, children, ...rest }) => (
  <div>
    <label htmlFor={id} className={label}>{children}</label>
    <input id={id} className={input} {...rest} />
  </div>
);

const ErrorBox = ({ msg }) => msg ? (
  <div className="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm" data-testid="cda-error">{msg}</div>
) : null;

const Steps = () => (
  <ol className="grid sm:grid-cols-3 gap-3 text-sm">
    {[
      ['1', 'You request', 'Name, company and work email. Nothing technical yet.'],
      ['2', 'We approve', 'A LAMKO team member reviews it personally, normally within one business day.'],
      ['3', 'You accept online', 'You receive a secure link, read the full text and accept electronically. Both sides get the executed copy.'],
    ].map(([n, t, d]) => (
      <li key={n} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="text-xs font-bold tracking-widest text-orange-600 uppercase">Step {n}</div>
        <div className="font-semibold text-gray-900 mt-1">{t}</div>
        <div className="text-gray-600 mt-1 leading-relaxed">{d}</div>
      </li>
    ))}
  </ol>
);

const AgreementText = ({ clauses, company, html }) => {
  if (html) {
    return <div className="cda-text" dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return (
    <div>
      {(clauses || []).map((c) => (
        <div key={c.heading} className="mt-4">
          <h3 className="text-sm font-semibold text-gray-900">{c.heading}</h3>
          <p className="text-[13px] leading-relaxed text-gray-700 mt-1">
            {c.body.replace('{{COUNTERPARTY}}', company || '[Counterparty]')}
          </p>
        </div>
      ))}
    </div>
  );
};

const ExecutionRecord = ({ ex }) => (
  <div className="rounded-lg border border-teal-200 bg-teal-50 p-4 text-sm">
    <div className="text-xs font-bold tracking-widest text-teal-700 uppercase mb-2">Executed agreement · record</div>
    <table className="w-full text-left">
      <tbody>
        {[
          ['Counterparty', ex.counterparty], ['Accepted by', `${ex.acceptedBy}, ${ex.title}`], ['Email', ex.email],
          ['Date and time', ex.at], ['Network address', ex.ip], ['Reference', ex.ref], ['Version', ex.version], ['SHA-256', ex.sha256],
        ].map(([k, v]) => (
          <tr key={k} className="align-top"><td className="pr-4 py-0.5 text-gray-500 whitespace-nowrap">{k}</td><td className="py-0.5 text-gray-900 break-all">{v}</td></tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ------------------------------------------------------------------ request */
const RequestMode = ({ source }) => {
  const [form, setForm] = useState({ name: '', company: '', email: '', note: '', consent: false, company_website: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const [text, setText] = useState(null);
  const [showText, setShowText] = useState(false);
  const started = useRef(Date.now());
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  useEffect(() => {
    fetch(`${API}/api/cda/text`).then((r) => r.json()).then(setText).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim() || !form.company.trim() || !form.email.trim()) { setErr('Please enter your name, company and work email.'); return; }
    if (!form.consent) { setErr('Please confirm you are authorised to request this agreement.'); return; }
    setBusy(true);
    try {
      const d = await post('request', { ...form, elapsed: Date.now() - started.current, source });
      setDone(d);
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className={card} data-testid="cda-request-success">
        <div className="text-xs font-bold tracking-widest text-teal-700 uppercase">Request received</div>
        <h2 className="text-xl font-semibold text-gray-900 mt-1">Thank you, {form.name.split(' ')[0]}.</h2>
        <p className="text-gray-700 mt-3 leading-relaxed">
          We sent a confirmation to <b>{form.email}</b>. A LAMKO team member will review the request personally, normally within one business day,
          and you will then receive a secure link to read and accept the agreement online.
        </p>
        <p className="text-sm text-gray-500 mt-3">Reference: {done.ref}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/partner" className={btnGhost}>Back to Partner services</Link>
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" className={btnGhost}>Book a meeting</a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={card}>
        <div className="text-xs font-bold tracking-widest text-orange-600 uppercase">Before you share anything confidential</div>
        <h2 className="text-xl font-semibold text-gray-900 mt-1">Request a mutual confidentiality agreement (CDA / NDA)</h2>
        <p className="text-gray-700 mt-3 leading-relaxed">
          Many customers prefer to have a signed agreement in place before they describe a molecule, a route, a formulation or a target quantity.
          LAMKO offers a standard mutual CDA that can be requested, approved and accepted entirely online, without printing or scanning.
          It is mutual, governed by Korean law, runs for three years, and protects both sides equally.
        </p>
        <div className="mt-5"><Steps /></div>
        <div className="mt-5 text-sm text-gray-600">
          <button type="button" onClick={() => setShowText((s) => !s)} className="text-teal-700 font-semibold underline underline-offset-2">
            {showText ? 'Hide the full agreement text' : 'Read the full agreement text first'}
          </button>
          {text?.version ? <span className="ml-2 text-gray-400">Version {text.version}</span> : null}
        </div>
        {showText ? (
          <div className="mt-3 max-h-[28rem] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="font-semibold text-gray-900">{text?.title}</h3>
            <AgreementText clauses={text?.clauses} company="[Your company]" />
          </div>
        ) : null}
      </div>

      <div className={card}>
        <h3 className="text-lg font-semibold text-gray-900">Your details</h3>
        <p className="text-sm text-gray-600 mt-1 mb-5">Only the LAMKO team member handling your request sees this. Three fields are enough; the agreement itself is completed at the acceptance step.</p>
        <ErrorBox msg={err} />
        <form onSubmit={submit} className="space-y-4" noValidate>
          <input style={{ position: 'absolute', left: '-9999px' }} tabIndex={-1} autoComplete="off" value={form.company_website} onChange={upd('company_website')} aria-hidden="true" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="cda-name" value={form.name} onChange={upd('name')} placeholder="e.g., Dr. Min-Jun Park" autoComplete="name">Full name <span className="text-red-600">*</span></Field>
            <Field id="cda-company" value={form.company} onChange={upd('company')} placeholder="Legal entity name" autoComplete="organization">Company <span className="text-red-600">*</span></Field>
          </div>
          <Field id="cda-email" type="email" value={form.email} onChange={upd('email')} placeholder="you@company.com" autoComplete="email">Work email <span className="text-red-600">*</span></Field>
          <div>
            <label htmlFor="cda-note" className={label}>Anything we should know? <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea id="cda-note" rows={3} className={input} value={form.note} onChange={upd('note')} placeholder="e.g., cosmetic active ingredient, OLED host material, expected timing. No structures or specifications yet, please." />
          </div>
          <label className="flex items-start gap-3 text-sm text-gray-700">
            <input type="checkbox" className="mt-1" checked={form.consent} onChange={upd('consent')} />
            <span>I am authorised to request this agreement for my company, and I agree that LAMKO stores these details to process the request. <Link to="/privacy-policy" className="underline">Privacy policy</Link>.</span>
          </label>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button type="submit" className={btnPrimary} disabled={busy} data-testid="cda-request-submit">{busy ? 'Sending...' : 'Request the CDA'}</button>
            <Link to="/partner" className={btnGhost}>Continue without a CDA</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ approve (owner) */
const ApproveMode = ({ token }) => {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    post('approve', { token, action: 'info' }).then(setInfo).catch((e) => setErr(e.message));
  }, [token]);
  const approve = async () => {
    setBusy(true); setErr('');
    try { const d = await post('approve', { token, action: 'approve' }); setInfo(d); setDone(true); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className={card}>
      <div className="text-xs font-bold tracking-widest text-orange-600 uppercase">LAMKO internal · approval</div>
      <h2 className="text-xl font-semibold text-gray-900 mt-1">CDA request {info?.ref ? `· ${info.ref}` : ''}</h2>
      <ErrorBox msg={err} />
      {info ? (
        <>
          <table className="mt-4 text-sm"><tbody>
            {[['Company', info.company], ['Name', info.name], ['Email', info.email], ['Note', info.note || '-'], ['Requested', info.at], ['IP', info.ip], ['Status', info.status]].map(([k, v]) => (
              <tr key={k}><td className="pr-4 py-0.5 text-gray-500">{k}</td><td className="py-0.5 text-gray-900">{v}</td></tr>
            ))}
          </tbody></table>
          {done || info.status === 'approved' || info.status === 'executed' ? (
            <p className="mt-5 text-sm text-teal-800 bg-teal-50 border border-teal-200 rounded-md p-3">
              {info.status === 'executed' ? 'This agreement has already been executed.' : 'Approved. The counterparty has been emailed a secure link to read and accept the agreement.'}
            </p>
          ) : (
            <button type="button" onClick={approve} disabled={busy} className={`${btnOrange} mt-5`}>{busy ? 'Approving...' : 'Approve and send the accept link'}</button>
          )}
        </>
      ) : !err ? <p className="text-sm text-gray-500 mt-3">Loading...</p> : null}
    </div>
  );
};

/* ------------------------------------------------------------------ accept (counterparty) */
const AcceptMode = ({ token }) => {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', title: '', consent: false });
  const [fwd, setFwd] = useState([{ name: '', email: '' }]);
  const [fwdMsg, setFwdMsg] = useState('');
  const [fwdBusy, setFwdBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  useEffect(() => {
    post('accept', { token, action: 'info' }).then((d) => { setInfo(d); if (d.signerName) setForm((f) => ({ ...f, name: d.signerName })); }).catch((e) => setErr(e.message));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault(); setErr('');
    if (!form.name.trim() || !form.title.trim()) { setErr('Please enter your full name and job title.'); return; }
    if (!form.consent) { setErr('Please confirm you are authorised to accept on behalf of your company.'); return; }
    setBusy(true);
    try { const d = await post('accept', { token, action: 'accept', ...form }); setInfo((i) => ({ ...i, ...d })); window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };

  const forward = async (e) => {
    e.preventDefault(); setFwdMsg(''); setFwdBusy(true);
    try {
      const colleagues = fwd.filter((c) => c.name.trim() && c.email.trim());
      if (!colleagues.length) throw new Error('Add at least one colleague (name and email).');
      const d = await post('forward', { token, from: form.name || info?.signerName || '', colleagues });
      setFwdMsg(d.sent?.length ? `Sent to ${d.sent.join(', ')}.` : 'No new addresses were sent (already included).');
      setFwd([{ name: '', email: '' }]);
    } catch (ex) { setFwdMsg(ex.message); } finally { setFwdBusy(false); }
  };

  if (!info) return <div className={card}><ErrorBox msg={err} />{!err ? <p className="text-sm text-gray-500">Loading the agreement...</p> : null}</div>;

  const executed = info.status === 'executed' && info.execution;
  return (
    <div className="space-y-6">
      <div className={card}>
        <div className="text-xs font-bold tracking-widest text-teal-700 uppercase">{executed ? 'Agreement in force' : 'Ready to accept'}</div>
        <h2 className="text-xl font-semibold text-gray-900 mt-1">{info.title}</h2>
        <p className="text-sm text-gray-600 mt-1">between LAMKO Co., Ltd. and <b>{info.company}</b> · {info.ref} · version {info.version}</p>
        {executed ? (
          <>
            <div className="mt-5"><ExecutionRecord ex={info.execution} /></div>
            <p className="text-sm text-gray-700 mt-4">The executed copy has been emailed to everyone involved. You can now share technical details safely.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to={`/partner?cda=${info.ref.replace('LAMKO-CDA-', '')}`} className={btnPrimary}>Continue to your enquiry</Link>
              <a href={BOOKING_URL} target="_blank" rel="noreferrer" className={btnOrange}>Book a meeting</a>
            </div>
          </>
        ) : info.status !== 'approved' ? (
          <p className="mt-4 text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded-md p-3">This agreement has not been approved by LAMKO yet. You will receive an email when it is ready.</p>
        ) : (
          <p className="text-sm text-gray-700 mt-4">Please read the full text below. If you are authorised to sign for your company, complete the acceptance at the bottom. If a colleague should sign instead, forward the link from the last section.</p>
        )}
      </div>

      <div className={card}>
        <h3 className="font-semibold text-gray-900">Full text</h3>
        <div className="mt-2 max-h-[34rem] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
          <AgreementText html={info.html} />
        </div>
      </div>

      {!executed && info.status === 'approved' ? (
        <div className={card}>
          <h3 className="font-semibold text-gray-900">Accept on behalf of {info.company}</h3>
          <p className="text-sm text-gray-600 mt-1 mb-4">Signing as {info.signerEmail}. Your name, title, email, the time (UTC), your network address and a SHA-256 fingerprint of this version are recorded as the acceptance record (clause 15).</p>
          <ErrorBox msg={err} />
          <form onSubmit={accept} className="space-y-4" noValidate>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="acc-name" value={form.name} onChange={upd('name')} placeholder="Full legal name">Full name <span className="text-red-600">*</span></Field>
              <Field id="acc-title" value={form.title} onChange={upd('title')} placeholder="e.g., R&D Director">Job title <span className="text-red-600">*</span></Field>
            </div>
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input type="checkbox" className="mt-1" checked={form.consent} onChange={upd('consent')} />
              <span>I have read the agreement, I am authorised to accept it on behalf of {info.company}, and I agree that clicking "I accept" is my electronic signature.</span>
            </label>
            <button type="submit" className={btnPrimary} disabled={busy} data-testid="cda-accept-submit">{busy ? 'Recording...' : 'I accept'}</button>
          </form>
        </div>
      ) : null}

      {info.status === 'approved' || executed ? (
        <div className={card}>
          <h3 className="font-semibold text-gray-900">Forward to a colleague</h3>
          <p className="text-sm text-gray-600 mt-1 mb-4">{executed ? 'Send the executed agreement to colleagues who should have a copy.' : 'If someone else should review or sign, send them their own secure link (up to 5 people).'}</p>
          <form onSubmit={forward} className="space-y-3" noValidate>
            {fwd.map((c, i) => (
              <div key={i} className="grid sm:grid-cols-2 gap-3">
                <input className={input} placeholder="Colleague name" value={c.name} onChange={(e) => setFwd((a) => a.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                <input className={input} type="email" placeholder="colleague@company.com" value={c.email} onChange={(e) => setFwd((a) => a.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
              </div>
            ))}
            <div className="flex flex-wrap gap-3 items-center">
              {fwd.length < 5 ? <button type="button" className={btnGhost} onClick={() => setFwd((a) => [...a, { name: '', email: '' }])}>Add another</button> : null}
              <button type="submit" className={btnPrimary} disabled={fwdBusy}>{fwdBusy ? 'Sending...' : 'Send link'}</button>
              {fwdMsg ? <span className="text-sm text-gray-700">{fwdMsg}</span> : null}
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

/* ------------------------------------------------------------------ page */
export const Cda = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const approve = params.get('approve');
  const accept = params.get('accept');
  const source = params.get('from') || (typeof document !== 'undefined' ? document.referrer : '') || '';
  useEffect(() => { document.title = 'Confidentiality Agreement (CDA) | LAMKO'; }, []);
  return (
    <div className="min-h-screen bg-gray-50">
      <InquiryHero title="Confidentiality Agreement (CDA)" subtitle="Mutual, online, in place before you share a structure, a route or a target quantity" />
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {approve ? <ApproveMode token={approve} /> : accept ? <AcceptMode token={accept} /> : <RequestMode source={source} />}
        </div>
      </section>
    </div>
  );
};

export default Cda;
